/**
 * Location d'équipement : une offre par article de la liste commune (canot, kayak, raquettes…), avec sa variante
 * (16 pieds, double…), la capacité, la quantité, le dépôt, l'âge minimum et ce qui est inclus. Les prix vont par
 * durée (heure, demi-journée, jour…) ; case vide = durée non offerte, « sd » = sur demande.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteRentalOffer, fetchTariffs, requestTariffListItem, saveRentalOffer, type RentalOfferRow } from '../../api'
import { cellText, errorText, numOrNull, parseCell, type TabProps } from './common'

export function RentalTab({ orgId, data, onSaved, onListChanged }: TabProps) {
  const { t } = useTranslation('gestion')
  const [newItem, setNewItem] = useState('')
  const [askName, setAskName] = useState('')
  const [askCategory, setAskCategory] = useState(data.choices.equipment_categories[0]?.[0] ?? '')
  const [error, setError] = useState<string | null>(null)

  const add = async (itemId: number) => {
    setError(null)
    try {
      onSaved(await saveRentalOffer(orgId, null, { item_id: itemId }))
    } catch (e) {
      setError(errorText(e))
    }
  }
  const ask = async () => {
    setError(null)
    try {
      const item = await requestTariffListItem(orgId, 'items', askName.trim(), { category: askCategory })
      onListChanged?.()
      await add(item.id)
      setAskName('')
    } catch (e) {
      setError(errorText(e))
    }
  }

  return (
    <>
      <p className="g-muted">{t('tarifs.location_aide')}</p>
      {data.rental_offers.length === 0 && <p className="g-hint">{t('tarifs.aucune_offre')}</p>}
      {data.rental_offers.map((offer) => (
        <RentalOfferEditor key={offer.id} orgId={orgId} data={data} offer={offer} onSaved={onSaved} />
      ))}
      <div className="g-tarifs__request">
        <select className="g-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}>
          <option value="">{t('tarifs.choisir_article')}</option>
          {data.choices.equipment_categories.map(([cat, label]) => (
            <optgroup key={cat} label={label}>
              {data.lists.items
                .filter((x) => x.category === cat)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.pending ? ` (${t('tarifs.en_attente')})` : ''}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!newItem}
          onClick={() => {
            void add(Number(newItem))
            setNewItem('')
          }}
        >
          <span className="mdi mdi-plus" aria-hidden /> {t('tarifs.ajouter_offre')}
        </button>
        <span className="g-muted">{t('tarifs.article_manquant')}</span>
        <input className="g-input" value={askName} placeholder={t('tarifs.nouvel_article')} onChange={(e) => setAskName(e.target.value)} />
        <select className="g-input" value={askCategory} onChange={(e) => setAskCategory(e.target.value)} aria-label={t('tarifs.categorie_article')}>
          {data.choices.equipment_categories.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <button type="button" className="btn" disabled={!askName.trim()} onClick={() => void ask()}>
          {t('tarifs.demander')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
    </>
  )
}

function RentalOfferEditor({ orgId, data, offer, onSaved }: { orgId: number; data: TabProps['data']; offer: RentalOfferRow; onSaved: TabProps['onSaved'] }) {
  const { t } = useTranslation('gestion')
  const includeTax = data.settings.prices_include_tax
  const [form, setForm] = useState({
    variant: offer.variant,
    length_ft: offer.length_ft ?? '',
    capacity: offer.capacity?.toString() ?? '',
    model: offer.model,
    quantity: offer.quantity?.toString() ?? '',
    deposit: offer.deposit ?? '',
    min_age: offer.min_age?.toString() ?? '',
    included: offer.included,
    reservation_required: offer.reservation_required,
    season: offer.season,
    note: offer.note,
  })
  const [prices, setPrices] = useState<Record<number, string>>(() => {
    const out: Record<number, string> = {}
    for (const p of offer.prices) if (p.category_id == null) out[p.duration_id] = cellText(p, includeTax)
    return out
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      onSaved(
        await saveRentalOffer(orgId, offer.id, {
          variant: form.variant,
          length_ft: form.length_ft.trim() ? form.length_ft.replace(',', '.') : null,
          capacity: numOrNull(form.capacity),
          model: form.model,
          quantity: numOrNull(form.quantity),
          deposit: form.deposit.trim() ? form.deposit.replace(',', '.').replace('$', '').trim() : null,
          min_age: numOrNull(form.min_age),
          included: form.included,
          reservation_required: form.reservation_required,
          season: form.season,
          note: form.note,
          prices: data.lists.durations.map((d) => ({ ...parseCell(prices[d.id] ?? '', null), duration_id: d.id })),
        }),
      )
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!window.confirm(t('tarifs.supprimer_offre', { name: offer.item }))) return
    await deleteRentalOffer(orgId, offer.id)
    onSaved(await fetchTariffs(orgId))
  }

  const text = (k: 'variant' | 'model' | 'included' | 'note', placeholder?: string) => (
    <label className="g-field">
      <span className="g-field__label">{t(`tarifs.offre_${k}`)}</span>
      <input className="g-input" value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
    </label>
  )
  const num = (k: 'length_ft' | 'capacity' | 'quantity' | 'deposit' | 'min_age') => (
    <label className="g-field">
      <span className="g-field__label">{t(`tarifs.offre_${k}`)}</span>
      <input className="g-input g-tarifs__num" inputMode="decimal" value={form[k]} onChange={(e) => set(k, e.target.value)} />
    </label>
  )

  return (
    <div className="g-tarifs__line">
      <div className="g-tarifs__line-head">
        <strong>
          {offer.item}
          {form.variant ? ` — ${form.variant}` : ''}
        </strong>
        <select className="g-input" value={form.season} onChange={(e) => set('season', e.target.value)} aria-label={t('tarifs.saison')}>
          {data.choices.seasons.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <label className="g-check">
          <input type="checkbox" checked={form.reservation_required} onChange={(e) => set('reservation_required', e.target.checked)} />
          <span>{t('tarifs.offre_reservation')}</span>
        </label>
      </div>
      <div className="g-tarifs__cells">
        {text('variant', t('tarifs.offre_variant_exemple'))}
        {num('length_ft')}
        {num('capacity')}
        {text('model')}
        {num('quantity')}
        {num('deposit')}
        {num('min_age')}
      </div>
      <div className="g-tarifs__cells">
        {data.lists.durations.map((d) => (
          <label key={d.id} className="g-field">
            <span className="g-field__label">{d.name}</span>
            <input className="g-input" value={prices[d.id] ?? ''} placeholder="—" onChange={(e) => setPrices((p) => ({ ...p, [d.id]: e.target.value }))} />
          </label>
        ))}
      </div>
      {text('included', t('tarifs.offre_included_exemple'))}
      {text('note')}
      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void save()}>
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
        <span className="g-spacer" />
        <button type="button" className="btn g-danger" onClick={() => void remove()}>
          {t('commun.supprimer')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
    </div>
  )
}
